import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import type { TimesheetDeadlineSettings } from '@/types/timesheetSettings';

const DAYS_OF_WEEK = [
  { value: 1 as const, label: 'Mon' },
  { value: 2 as const, label: 'Tue' },
  { value: 3 as const, label: 'Wed' },
  { value: 4 as const, label: 'Thu' },
  { value: 5 as const, label: 'Fri' },
  { value: 6 as const, label: 'Sat' },
  { value: 7 as const, label: 'Sun' },
] as const;

interface ManagerDigestSettingsProps {
  settings: TimesheetDeadlineSettings;
  onChange: (settings: TimesheetDeadlineSettings, isValid: boolean) => void;
}

export const ManagerDigestSettings: React.FC<ManagerDigestSettingsProps> = ({
  settings,
  onChange,
}) => {
  const enableManagerDigest = settings.enableManagerDigest ?? false;
  const digestDayOfWeek = settings.digestDayOfWeek ?? 1;

  const handleToggleChange = (checked: boolean) => {
    const newSettings: TimesheetDeadlineSettings = {
      ...settings,
      enableManagerDigest: checked,
    };
    onChange(newSettings, true);
  };

  const handleDayChange = (day: 1 | 2 | 3 | 4 | 5 | 6 | 7) => {
    const newSettings: TimesheetDeadlineSettings = {
      ...settings,
      digestDayOfWeek: day,
    };
    onChange(newSettings, true);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manager Weekly Digest</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Enable Manager Digest toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="enableManagerDigest">Weekly digest for managers</Label>
            <p className="text-sm text-muted-foreground">
              Enable weekly summaries for managers with outstanding team timesheets
            </p>
          </div>
          <Switch
            id="enableManagerDigest"
            checked={enableManagerDigest}
            onCheckedChange={handleToggleChange}
          />
        </div>

        {/* Day of week selector — only shown when toggle is on */}
        {enableManagerDigest && (
          <div className="pl-4 border-l-2 border-muted space-y-3">
            <Label>Send digest on:</Label>

            {/* Segmented day picker */}
            <div className="flex gap-1 flex-wrap">
              {DAYS_OF_WEEK.map((day) => (
                <Button
                  key={day.value}
                  type="button"
                  variant={digestDayOfWeek === day.value ? 'default' : 'outline'}
                  size="sm"
                  className="w-12"
                  onClick={() => handleDayChange(day.value)}
                  aria-pressed={digestDayOfWeek === day.value}
                >
                  {day.label}
                </Button>
              ))}
            </div>

            {/* Time clarification */}
            <p className="text-xs text-muted-foreground">
              The digest will be sent at 07:00 UTC on the selected day.
            </p>

            {/* Recipient clarification */}
            <p className="text-sm text-muted-foreground">
              Digests are sent to managers with direct reports who have outstanding timesheets.
            </p>
          </div>
        )}

      </CardContent>
    </Card>
  );
};
